import { getSubmissionActivityLogId } from './studentAssignmentTrackingStatic';

const SNIPPET_CAP = 10;

function num(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function mergeSnippetLists(lists) {
    const merged = [];
    for (const list of lists) {
        if (!Array.isArray(list)) continue;
        for (const entry of list) {
            const text = typeof entry === 'string' ? entry.trim() : String(entry ?? '').trim();
            if (text) merged.push(text);
        }
    }
    return merged.slice(-SNIPPET_CAP);
}

export function buildTrackingLogSources(pastSubmissions, currentSubmission) {
    const sources = [];
    const seen = new Set();
    const parentSubmissionId =
        currentSubmission?.id != null
            ? String(currentSubmission.id)
            : pastSubmissions?.[0]?.submissionId != null
              ? String(pastSubmissions[0].submissionId)
              : null;

    const pushSource = (submission, fallbackIndex) => {
        const logId = getSubmissionActivityLogId(submission);
        if (!logId || seen.has(logId)) return;
        seen.add(logId);
        const subNum = submission?.submissions;
        const label =
            subNum != null && subNum !== ''
                ? `Submission-${subNum}`
                : `Submission-${fallbackIndex}`;
        sources.push({
            logId,
            label,
            submissionId: parentSubmissionId,
            recordId: submission?.id != null ? String(submission.id) : null,
        });
    };

    if (Array.isArray(pastSubmissions)) {
        pastSubmissions.forEach((pt, i) => {
            pushSource(pt, i + 1);
        });
    }
    if (currentSubmission) {
        const fallback = (Array.isArray(pastSubmissions) ? pastSubmissions.length : 0) + 1;
        pushSource(currentSubmission, fallback);
    }
    return sources;
}

export function aggregateActivityMetas(attemptMetas) {
    const attempts = (attemptMetas || []).filter((m) => m && typeof m === 'object');
    if (attempts.length === 0) return {};

    const numericKeys = [
        'duration_sec',
        'resume_count',
        'finish_later_count',
        'page_visibility_change_count',
        'copy_attempt_count',
        'paste_attempt_count',
        'context_menu_attempt_count',
        'retake_sessions_count',
        'window_blur_count',
    ];

    const agg = {};
    for (const key of numericKeys) {
        agg[key] = attempts.reduce((sum, m) => sum + num(m[key]), 0);
    }

    agg.copy_content_snippets = mergeSnippetLists(attempts.map((m) => m.copy_content_snippets));
    agg.paste_content_snippets = mergeSnippetLists(attempts.map((m) => m.paste_content_snippets));

    const sessionSet = new Set();
    const browserSet = new Set();
    let earliestStart = null;
    let latestSessionId = null;
    let latestDevice = null;
    let maxTrackingVersion = 0;

    for (const m of attempts) {
        (Array.isArray(m.session_ids) ? m.session_ids : []).forEach((s) => {
            if (s != null && String(s).trim()) sessionSet.add(String(s).trim());
        });
        (Array.isArray(m.browser_history) ? m.browser_history : []).forEach((b) => {
            if (b != null && String(b).trim()) browserSet.add(String(b).trim());
        });
        const started = m.assignment_session_started_at;
        if (started && typeof started === 'string') {
            if (!earliestStart || started < earliestStart) earliestStart = started;
        }
        if (m.last_session_id != null && String(m.last_session_id).trim()) {
            latestSessionId = String(m.last_session_id).trim();
        }
        if (m.last_device != null && String(m.last_device).trim()) {
            latestDevice = String(m.last_device).trim();
        }
        maxTrackingVersion = Math.max(maxTrackingVersion, num(m.tracking_version));
    }

    agg.session_ids = Array.from(sessionSet);
    agg.browser_history = Array.from(browserSet);
    agg.last_session_id = latestSessionId;
    agg.last_device = latestDevice;
    agg.assignment_session_started_at = earliestStart;
    agg.tracking_version = maxTrackingVersion || null;
    agg._aggregated_attempt_count = attempts.length;

    const replayBySession = new Map();
    for (const m of attempts) {
        const list = Array.isArray(m.session_replays) ? m.session_replays : [];
        for (const entry of list) {
            if (!entry || typeof entry !== 'object') continue;
            const sid = entry.session_id ?? entry.sessionId;
            if (!sid) continue;
            const logKey = entry.log_id ?? entry.logId ?? m.log_id ?? m.logId ?? '';
            const subKey = entry.submission_id ?? entry.submissionId ?? '';
            const key = `${logKey}:${subKey}:${String(sid)}`;
            const prev = replayBySession.get(key) || { session_id: key, chunks: [], started_at: null, updated_at: null };
            const chunkMap = new Map();
            for (const c of [...(prev.chunks || []), ...(Array.isArray(entry.chunks) ? entry.chunks : [])]) {
                if (!c || typeof c !== 'object') continue;
                const idx = Number(c.index ?? c.chunk_index);
                const url = c.url ?? c.firebase_url;
                if (!Number.isFinite(idx) || !url) continue;
                chunkMap.set(idx, {
                    index: idx,
                    url: String(url),
                    event_count: Number(c.event_count) || 0,
                    recorded_at: c.recorded_at ?? null,
                });
            }
            replayBySession.set(key, {
                session_id: key,
                started_at: entry.started_at || prev.started_at,
                updated_at: entry.updated_at || prev.updated_at,
                chunks: Array.from(chunkMap.values()).sort((a, b) => a.index - b.index),
            });
        }
    }
    agg.session_replays = Array.from(replayBySession.values()).sort((a, b) =>
        String(b.updated_at || '').localeCompare(String(a.updated_at || ''))
    );

    return agg;
}

export function buildSessionReplayTeacherSummary(agg) {
    const sessions = Array.isArray(agg?.session_replays) ? agg.session_replays : [];
    const teacherSessions = sessions
        .map((session, sessionIdx) => {
            const sid = session?.session_id ? String(session.session_id) : '';
            const chunks = (Array.isArray(session.chunks) ? session.chunks : [])
                .filter((c) => c?.url)
                .map((c) => ({
                    index: Number(c.index ?? c.chunk_index),
                    url: String(c.url),
                    eventCount: Number(c.event_count) || 0,
                    recordedAt: c.recorded_at ?? session.updated_at ?? null,
                }))
                .sort((a, b) => a.index - b.index);
            if (!chunks.length) return null;
            const shortId = sid ? sid.slice(0, 8) : String(sessionIdx + 1);
            return {
                sessionId: sid,
                label: `Work session ${shortId}`,
                startedAt: session.started_at ?? null,
                updatedAt: session.updated_at ?? null,
                chunks,
            };
        })
        .filter(Boolean)
        .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));

    const chunkCount = teacherSessions.reduce((n, s) => n + s.chunks.length, 0);
    return {
        hasContent: teacherSessions.length > 0,
        sessionCount: teacherSessions.length,
        chunkCount,
        sessions: teacherSessions,
    };
}

export function parseUserAgentForTeacher(ua) {
    if (ua == null || typeof ua !== 'string') return 'Unknown device';
    const s = ua.trim();
    if (!s) return 'Unknown device';

    let browser = '';
    if (/Edg\//i.test(s)) browser = 'Microsoft Edge';
    else if (/OPR\/|Opera/i.test(s)) browser = 'Opera';
    else if (/Chrome\/|CriOS/i.test(s)) browser = 'Chrome';
    else if (/Firefox\/|FxiOS/i.test(s)) browser = 'Firefox';
    else if (/Safari\//i.test(s) && !/Chrome/i.test(s)) browser = 'Safari';

    let os = '';
    if (/iPhone|iPod/i.test(s)) os = 'iPhone';
    else if (/iPad/i.test(s) || (/Macintosh/i.test(s) && /Mobile/i.test(s))) os = 'iPad';
    else if (/Android/i.test(s)) os = /Mobile/i.test(s) ? 'Android phone' : 'Android tablet';
    else if (/Windows NT/i.test(s)) os = 'Windows computer';
    else if (/Macintosh|Mac OS X/i.test(s)) os = 'Mac computer';
    else if (/CrOS/i.test(s)) os = 'Chromebook';
    else if (/Linux/i.test(s)) os = 'Linux computer';

    if (browser && os) return `${browser} on ${os}`;
    if (os) return os;
    if (browser) return browser;
    return 'Unknown device';
}

export function buildSessionBrowserTeacherSummary(agg) {
    const sessionIds = Array.isArray(agg?.session_ids) ? agg.session_ids : [];
    const browserHistory = Array.isArray(agg?.browser_history) ? agg.browser_history : [];
    const lastDevice =
        agg?.last_device != null && String(agg.last_device).trim() ? String(agg.last_device).trim() : '';

    const uaSources = [...browserHistory];
    if (lastDevice && !uaSources.includes(lastDevice)) {
        uaSources.push(lastDevice);
    }

    const deviceLabels = [];
    const seenLabels = new Set();
    for (const ua of uaSources) {
        const label = parseUserAgentForTeacher(ua);
        if (seenLabels.has(label)) continue;
        seenLabels.add(label);
        deviceLabels.push(label);
    }

    const workSessionCount = sessionIds.length;
    const workSessionSwitches = Math.max(0, workSessionCount - 1);
    const deviceChangeCount = Math.max(0, deviceLabels.length - 1);
    const primaryDevice = deviceLabels.length ? deviceLabels[deviceLabels.length - 1] : null;

    let summaryLine = '';
    if (deviceLabels.length > 1) {
        summaryLine = `${deviceLabels.length} devices`;
    } else if (primaryDevice) {
        summaryLine = primaryDevice;
    } else if (workSessionCount > 1) {
        summaryLine = `${workSessionCount} work sessions`;
    } else if (workSessionCount === 1) {
        summaryLine = '1 work session';
    }

    return {
        workSessionCount,
        workSessionSwitches,
        deviceLabels,
        deviceChangeCount,
        primaryDevice,
        summaryLine,
        hasContent: workSessionCount > 0 || deviceLabels.length > 0,
    };
}

export function buildPerAttemptSummary(sources, attemptMetas) {
    return (sources || []).map((source, index) => {
        const m = attemptMetas[index] || {};
        return {
            label: source.label,
            logId: source.logId,
            duration_sec: num(m.duration_sec),
            copy_attempt_count: num(m.copy_attempt_count),
            paste_attempt_count: num(m.paste_attempt_count),
            page_visibility_change_count: num(m.page_visibility_change_count),
            window_blur_count: num(m.window_blur_count),
            resume_count: num(m.resume_count),
            finish_later_count: num(m.finish_later_count),
        };
    });
}

function buildSwitchedAwayInsightDetail(tabShifts, blur) {
    const total = tabShifts + blur;
    if (total <= 0) return '';
    if (tabShifts > 0 && blur > 0) {
        return `The student left this assignment ${total} times — ${tabShifts} by opening another tab or page, and ${blur} by clicking to another app or window. They may have been doing other work at the same time.`;
    }
    if (tabShifts > 0) {
        const tabWord = tabShifts === 1 ? 'time' : 'times';
        return `The student opened another tab or page ${tabShifts} ${tabWord} while working. They may have been doing other work at the same time.`;
    }
    const blurWord = blur === 1 ? 'time' : 'times';
    return `The student clicked away to another app or window ${blur} ${blurWord} while working. They may have been doing other work at the same time.`;
}

export function buildBehaviorInsights(agg, attemptCount, viewMode) {
    const insights = [];
    const copy = num(agg.copy_attempt_count);
    const paste = num(agg.paste_attempt_count);
    const duration = num(agg.duration_sec);
    const tabShifts = num(agg.page_visibility_change_count);
    const blur = num(agg.window_blur_count);
    const resume = num(agg.resume_count);
    const finishLater = num(agg.finish_later_count);
    const contextMenu = num(agg.context_menu_attempt_count);
    const sessionCount = Array.isArray(agg.session_ids) ? agg.session_ids.length : 0;
    const browserCount = Array.isArray(agg.browser_history) ? agg.browser_history.length : 0;
    const clipboardTotal = copy + paste;
    const distractionTotal = tabShifts + blur;
    const hours = duration > 0 ? duration / 3600 : 0;
    const distractionsPerHour = hours > 0 ? distractionTotal / hours : distractionTotal;

    if (viewMode === 'overall' && attemptCount > 1) {
        const attemptWord = attemptCount === 1 ? 'time' : 'times';
        insights.push({
            severity: 'info',
            title: 'All attempts combined',
            detail: `These totals add up ${attemptCount} ${attemptWord} the student worked on this assignment (for example Submission-1, Submission-2, and the latest try). We only show activity that was already saved while they worked — nothing new is added here.`,
        });
    }

    if (clipboardTotal === 0 && duration > 120) {
        insights.push({
            severity: 'success',
            title: 'No copy or paste noticed',
            detail: 'The student did not copy, cut, or paste text during the time shown in this view.',
        });
    } else if (clipboardTotal >= 3) {
        insights.push({
            severity: 'warning',
            title: 'Copy or paste used',
            detail: `${copy} copy or cut and ${paste} paste — check the snippets below if any text was captured.`,
        });
    }

    if (resume + finishLater >= 2) {
        insights.push({
            severity: 'info',
            title: 'Multiple work sessions',
            detail: `${resume} resume(s) and ${finishLater} finish-later — student left and returned.`,
        });
    } else if (finishLater >= 1) {
        insights.push({
            severity: 'info',
            title: 'Saved and left',
            detail: 'Student used finish later at least once before returning.',
        });
    }

    if (distractionTotal >= 8 || (hours >= 0.25 && distractionsPerHour >= 40)) {
        insights.push({
            severity: 'warning',
            title: 'Often left the assignment',
            detail: buildSwitchedAwayInsightDetail(tabShifts, blur),
        });
    }

    if (sessionCount > 1) {
        insights.push({
            severity: 'info',
            title: 'Came back in a new work session',
            detail: `Student opened the assignment ${sessionCount} separate times (e.g. new visit, new tab, or after signing in again).`,
        });
    }

    if (browserCount > 1) {
        const deviceSummary = buildSessionBrowserTeacherSummary(agg);
        const deviceList = deviceSummary.deviceLabels.slice(0, 4).join(', ');
        insights.push({
            severity: 'warning',
            title: 'More than one device or browser',
            detail: deviceList
                ? `Work may span: ${deviceList}${deviceSummary.deviceLabels.length > 4 ? ', …' : ''}. Open Devices & sessions below for details.`
                : 'Different browsers or devices were detected during this work.',
        });
    }

    if (contextMenu >= 5) {
        insights.push({
            severity: 'info',
            title: 'Right-click usage',
            detail: `${contextMenu} context menu opens recorded.`,
        });
    }

    if (duration > 0 && attemptCount > 1 && viewMode === 'overall') {
        const avgMin = Math.round(duration / attemptCount / 60);
        insights.push({
            severity: 'info',
            title: 'Time per attempt (avg)',
            detail: `About ${avgMin} min average tracked time per attempt (${Math.round(duration / 60)} min total).`,
        });
    }

    if (insights.length === 0) {
        insights.push({
            severity: 'success',
            title: 'Steady work pattern',
            detail: 'Nothing unusual stood out for heavy copying, pasting, or switching away from the assignment in this view.',
        });
    }

    return insights;
}
